/**
 * showUndoToast (PP-2) — the builder's one "this mutation has a way back".
 *
 * Moved verbatim from shell.js so shell-free modules (section-clipboard, the
 * page manager's callers, tests) can import it without dragging the shell's
 * import graph along; shell.js re-exports it, so every existing
 * `import { showUndoToast } from './shell.js'` is untouched.
 *
 * The contract that matters: the toast snapshots the CURRENT history entry
 * and its Undo only fires `store.undo()` if that entry is still current. A
 * later mutation makes the toast a no-op rather than undoing the wrong thing;
 * the shell's `history-changed` sweep dismisses every `undo-` id on any
 * position change, which is the same guarantee from the other side.
 *
 * Call AFTER the transaction has committed and (for section adds) after the
 * render: the sweep runs on the store event, so a toast created before it
 * would be dismissed on its way in.
 */

import { store } from '../core/state.js';
import { showTransactionalUndoToast } from './toast.js';

/**
 * @param {string} message - human-readable label, e.g. "Section removed"
 * @param {object} [opts]
 * @param {number} [opts.duration=5000] - undo window in ms (converted to seconds)
 * @param {string} [opts.id] - dedupe id; commits+replaces an existing undo with the same id
 */
export function showUndoToast(message, opts = {}) {
  const { duration = 5000, id } = opts;
  const snapshotPosition = store.historyPosition;
  const snapshotEntry = store.history.entries[snapshotPosition] || null;
  const toastId = id || `undo-${snapshotPosition}-${Date.now()}`;
  return showTransactionalUndoToast(message, {
    seconds: Math.max(1, Math.ceil(duration / 1000)), // D6: short custom durations never round to 0s
    id: toastId,
    onUndo: () => {
      const currentEntry = store.history.entries[store.historyPosition] || null;
      if (snapshotEntry && currentEntry === snapshotEntry) store.undo();
    }
  });
}
